import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { AdminMembership, AdminPersonView, Role, WorkspaceAdminView } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button } from "../../../ui/Button";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Pill } from "../../../ui/Pill";
import { showToast } from "../../../ui/toastStore";
import { formatWhen } from "../shared/format";
import { GrantDialog, ResetPasswordDialog, RoleBoxes } from "./PersonDialogs";
import { usePeopleCache } from "./useAdminPeople";

function sameRoles(a: readonly Role[], b: readonly Role[]): boolean {
  return a.length === b.length && a.every((role) => b.includes(role));
}

/** One membership: the roles in a discipline (both may be ticked) and whether it is switched on. */
function MembershipEditor({ person, membership }: { person: AdminPersonView; membership: AdminMembership }) {
  const change = useApiCommand("change_roles");
  const cache = usePeopleCache();
  const [roles, setRoles] = useState<Role[]>(membership.roles);
  const [enabled, setEnabled] = useState(membership.enabled);
  const [problem, setProblem] = useState<string | null>(null);
  const dirty = !sameRoles(roles, membership.roles) || enabled !== membership.enabled;

  const save = async () => {
    if (roles.length === 0) return;
    setProblem(null);
    try {
      const reply = await change.mutateAsync({ id: membership.id, roles, enabled });
      cache.replace(reply.data);
      showToast({ text: `Roles saved for ${person.display_name} in ${membership.workspace_code}.` });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <li className="ra-membership">
      <div className="row">
        <strong>{membership.workspace_code}</strong>
        {!membership.enabled && <Pill>Switched off</Pill>}
      </div>
      <RoleBoxes roles={roles} onChange={setRoles} disabled={change.isPending} />
      <label className="ra-checkbox">
        <input type="checkbox" checked={enabled} disabled={change.isPending} onChange={(event) => setEnabled(event.target.checked)} /> Switched on
      </label>
      {roles.length === 0 && <p className="field__error">Choose at least one role. To take access away, switch the membership off.</p>}
      {dirty && (
        <Button variant="secondary" busy={change.isPending} disabled={roles.length === 0} onClick={() => void save()} aria-label={`Save the roles of ${person.display_name} in ${membership.workspace_code}`}>
          Save the roles
        </Button>
      )}
      {problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
    </li>
  );
}

interface PersonRowProps {
  person: AdminPersonView;
  workspaces: readonly WorkspaceAdminView[];
}

/** One account: who they are, whether they are online, whether they use their own key (never the key), their roles, and the admin's steps. */
export function PersonRow({ person, workspaces }: PersonRowProps) {
  const me = useCurrentMe();
  const cache = usePeopleCache();
  const disable = useApiCommand("disable_account");
  const enable = useApiCommand("enable_account");
  const [dialog, setDialog] = useState<null | "reset" | "grant" | "disable">(null);
  const [problem, setProblem] = useState<string | null>(null);
  const itself = person.id === me.person.id;

  const toggle = async () => {
    setProblem(null);
    try {
      const reply = person.enabled ? await disable.mutateAsync({ id: person.id }) : await enable.mutateAsync({ id: person.id });
      cache.replace(reply.data);
      setDialog(null);
      showToast({ text: person.enabled ? `${person.display_name} is disabled. Their work and their name stay.` : `${person.display_name} can sign in again.` });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <li className="ra-person" data-enabled={String(person.enabled)} data-person={person.username}>
      <div className="ra-person__head">
        <div>
          <strong className="ra-person__name">{person.display_name}</strong> <span className="muted">({person.username})</span>
        </div>
        <div className="row ra-person__marks">
          {person.is_admin && <Pill>Admin</Pill>}
          {!person.enabled && <Pill>Disabled</Pill>}
          {person.active_now && <Pill>Active now</Pill>}
          {person.uses_own_key && <Pill>Uses their own key</Pill>}
          {person.must_change_password && <Pill>First password not changed yet</Pill>}
        </div>
      </div>
      <p className="muted ra-person__signin">{person.last_sign_in_at ? `Last signed in ${formatWhen(person.last_sign_in_at)}.` : "Has not signed in yet."}</p>

      {person.memberships.length === 0 ? (
        <p className="muted">{person.is_admin ? "The admin account holds no discipline and stays apart from the Engineer and SME roles." : "Not in any discipline yet."}</p>
      ) : (
        <ul className="ra-memberships" aria-label={`Disciplines of ${person.display_name}`}>
          {person.memberships.map((membership) => (
            <MembershipEditor key={`${membership.id}:${membership.roles.join()}:${membership.enabled}`} person={person} membership={membership} />
          ))}
        </ul>
      )}

      <div className="ra-person__actions row">
        {!person.is_admin && (
          <Button onClick={() => setDialog("grant")} aria-label={`Add ${person.display_name} to a discipline`}>
            Add to a discipline
          </Button>
        )}
        <Button onClick={() => setDialog("reset")} aria-label={`Set a first password for ${person.display_name}`}>
          Set a first password
        </Button>
        {!itself &&
          (person.enabled ? (
            <Button variant="quiet" onClick={() => setDialog("disable")} aria-label={`Disable ${person.display_name}`}>
              Disable
            </Button>
          ) : (
            <Button variant="secondary" busy={enable.isPending} onClick={() => void toggle()} aria-label={`Enable ${person.display_name}`}>
              Enable
            </Button>
          ))}
      </div>
      {problem && dialog === null && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}

      {dialog === "reset" && <ResetPasswordDialog person={person} onClose={() => setDialog(null)} />}
      {dialog === "grant" && <GrantDialog person={person} workspaces={workspaces} onClose={() => setDialog(null)} />}
      <ConfirmDialog
        open={dialog === "disable"}
        title={`Disable ${person.display_name}?`}
        message="Their sessions end, their live connections close and the elements they were editing are released. Their work and their name stay on everything they did. You can enable the account again."
        confirmLabel="Disable the account"
        cancelLabel="Keep it enabled"
        busy={disable.isPending}
        error={problem}
        onConfirm={() => void toggle()}
        onCancel={() => setDialog(null)}
      />
    </li>
  );
}
